import React, { useState, useEffect } from "react";
import {
  MessageSquare,
  Send,
  Check,
  ArrowDownLeft,
  ArrowUpRight,
  Sparkles,
} from "lucide-react";
import { useApp } from "../../context/AppContext";
import { CONTACT_LOG_TYPES, CONTACT_PURPOSES } from "../../constants/contactTypes";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../../constants/appConfig";
import { Modal } from "../ui/Modal";
import { ActionButton } from "../ui/ActionButton";
import { FormInput } from "../ui/FormControls";
import { ContactLogIcon } from "../donors/DonorParts";
import {
  EMAIL_TEMPLATE_DEFINITIONS,
  getTemplateDefinition,
} from "../../constants/emailTemplates";
import {
  replaceTokens,
  buildEmailContext,
} from "../../utils/receiptTokens";
import { supabase } from "../../lib/supabase";

export const QuickLogModal = ({
  member,
  onClose,
  initialType = "email",
  autoSubject = "",
  editEntry = null,
  defaultPurpose = null,
  defaultTemplate = null,
}) => {
  const {
    palette,
    addContactLog,
    updateContactLog,
    chapterSettings,
    emailTemplates,
  } = useApp();
  const isEdit = !!editEntry;
  const [type, setType] = useState(isEdit ? editEntry.type : initialType);
  const [direction, setDirection] = useState(isEdit ? (editEntry.direction || "outbound") : "outbound");
  const [subject, setSubject] = useState(isEdit ? (editEntry.subject || "") : autoSubject);
  const [notes, setNotes] = useState(isEdit ? (editEntry.notes || "") : "");
  const [purpose, setPurpose] = useState(isEdit ? (editEntry.purpose || []) : (defaultPurpose || []));
  const [templateKey, setTemplateKey] = useState("");
  const [giftSummary, setGiftSummary] = useState(null);
  const typeInfo = CONTACT_LOG_TYPES.find((t) => t.id === type);

  // Reset direction when type changes
  // Types without direction are always "inbound" (they always score)
  useEffect(() => {
    if (typeInfo && !typeInfo.hasDirection) {
      setDirection("inbound");
    }
  }, [type, typeInfo]);

  // Load gift summary once for token substitution (last gift, totals).
  // Skipped in edit mode — templates don't apply to existing entries.
  useEffect(() => {
    if (isEdit || !member?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase
          .from("gifts")
          .select("amount, gift_date")
          .eq("member_id", member.id)
          .order("gift_date", { ascending: false })
          .limit(500);
        if (error || !data || cancelled) return;
        if (data.length === 0) {
          setGiftSummary({ lastGiftDate: null, lastGiftAmount: 0, totalGiving: 0 });
          return;
        }
        const totalGiving = data.reduce(
          (sum, g) => sum + (Number(g.amount) || 0),
          0
        );
        setGiftSummary({
          lastGiftDate: data[0].gift_date,
          lastGiftAmount: Number(data[0].amount) || 0,
          totalGiving,
        });
      } catch (_err) {
        // silent — templates just render fallback copy
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [member?.id, isEdit]);

  const applyTemplate = (nextKey) => {
    setTemplateKey(nextKey);
    if (!nextKey) return;
    const def = getTemplateDefinition(nextKey);
    const template = emailTemplates?.[nextKey];
    if (!def || !template) return;
    const context = buildEmailContext(member, chapterSettings, giftSummary);
    setSubject(replaceTokens(template.subject, context));
    setNotes(replaceTokens(template.body, context));
    setDirection("outbound");
    if (def.defaultPurpose && def.defaultPurpose.length > 0) {
      setPurpose(def.defaultPurpose);
    }
  };

  // Apply defaultTemplate once the gift summary has loaded (so tokens
  // resolve correctly on the first render the template is applied).
  useEffect(() => {
    if (isEdit) return;
    if (!defaultTemplate) return;
    if (templateKey) return; // user already picked something
    if (giftSummary === null) return; // wait for data before substituting
    applyTemplate(defaultTemplate);
  }, [defaultTemplate, giftSummary, isEdit]);

  const handleSave = () => {
    const payload = {
      type,
      direction: typeInfo?.hasDirection ? direction : "inbound",
      subject: subject.trim(),
      notes: notes.trim(),
      purpose: purpose.length > 0 ? purpose : null,
    };
    if (isEdit) {
      updateContactLog(member.id, editEntry.id, payload);
    } else {
      addContactLog(member.id, payload);
      if (type === "email" && direction === "outbound" && member.email) {
        const params = [];
        if (subject) params.push(`subject=${encodeURIComponent(subject)}`);
        if (notes) params.push(`body=${encodeURIComponent(notes)}`);
        const qs = params.length > 0 ? `?${params.join("&")}` : "";
        window.open(`mailto:${member.email}${qs}`, "_blank");
      }
    }
    onClose();
  };

  const showDirectionToggle = typeInfo?.hasDirection === true;

  return (
    <Modal
      title={`${isEdit ? "Edit" : "Log"} Contact — ${member.firstName} ${member.lastName}`}
      icon={MessageSquare}
      onClose={onClose}
      width={480}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {/* ── Direction Toggle ── */}
        {showDirectionToggle && (
          <div>
            <label
              style={{
                fontSize: FONT_SIZES.sm,
                fontWeight: 600,
                color: palette.text1,
                display: "block",
                marginBottom: 8,
              }}
            >
              Direction
            </label>
            <div
              style={{
                display: "flex",
                borderRadius: 10,
                overflow: "hidden",
                border: `2px solid ${palette.cardBorder}`,
                width: "fit-content",
              }}
            >
              <button
                onClick={() => setDirection("outbound")}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "9px 18px",
                  border: "none",
                  cursor: "pointer",
                  fontSize: FONT_SIZES.sm,
                  fontWeight: 700,
                  fontFamily: FONT_FAMILY,
                  background:
                    direction === "outbound"
                      ? palette.accent1
                      : "transparent",
                  color:
                    direction === "outbound"
                      ? "#fff"
                      : palette.text1,
                  transition: "all 0.15s",
                }}
              >
                <ArrowUpRight size={ICON_SIZES.sm} />
                Outbound
              </button>
              <button
                onClick={() => setDirection("inbound")}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "9px 18px",
                  border: "none",
                  borderLeft: `1px solid ${palette.cardBorder}`,
                  cursor: "pointer",
                  fontSize: FONT_SIZES.sm,
                  fontWeight: 700,
                  fontFamily: FONT_FAMILY,
                  background:
                    direction === "inbound" ? "#10b981" : "transparent",
                  color:
                    direction === "inbound"
                      ? "#fff"
                      : palette.text1,
                  transition: "all 0.15s",
                }}
              >
                <ArrowDownLeft size={ICON_SIZES.sm} />
                Inbound
              </button>
            </div>
            {/* Scoring hint */}
            <div
              style={{
                fontSize: 11,
                fontWeight: 500,
                marginTop: 6,
                color:
                  direction === "inbound"
                    ? "#059669"
                    : palette.text1,
                fontFamily: FONT_FAMILY,
              }}
            >
              {direction === "inbound"
                ? `✓ Inbound — scores ${
                    type === "email"
                      ? "2.5"
                      : type === "phone"
                      ? "2.5"
                      : type === "text"
                      ? "2.5"
                      : "0"
                  } engagement points`
                : "Outbound — recorded but does not score engagement points"}
            </div>
          </div>
        )}

        {/* ── Contact Type Selector ── */}
        <div>
          <label
            style={{
              fontSize: FONT_SIZES.sm,
              fontWeight: 600,
              color: palette.text1,
              display: "block",
              marginBottom: 8,
            }}
          >
            Contact Type
          </label>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {CONTACT_LOG_TYPES.map((ct) => (
              <button
                key={ct.id}
                onClick={() => setType(ct.id)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "8px 14px",
                  borderRadius: 10,
                  cursor: "pointer",
                  transition: "all 0.2s",
                  border:
                    type === ct.id
                      ? `2px solid ${ct.color}`
                      : `2px solid ${palette.cardBorder}`,
                  background: type === ct.id ? `${ct.color}12` : "transparent",
                  color: type === ct.id ? ct.color : palette.text1,
                  fontSize: FONT_SIZES.sm,
                  fontWeight: 600,
                  fontFamily: FONT_FAMILY,
                }}
              >
                <ContactLogIcon type={ct.id} size={ICON_SIZES.sm} />
                {ct.name}
              </button>
            ))}
          </div>
        </div>

        {/* ── Email Template picker (email type, new entries only) ── */}
        {!isEdit && type === "email" && (
          <div>
            <label
              style={{
                fontSize: FONT_SIZES.sm,
                fontWeight: 600,
                color: palette.text1,
                display: "flex",
                alignItems: "center",
                gap: 6,
                marginBottom: 8,
              }}
            >
              <Sparkles size={ICON_SIZES.sm} />
              Start from a template
              <span style={{ fontWeight: 400, color: palette.text3 }}>
                (optional)
              </span>
            </label>
            <select
              value={templateKey}
              onChange={(e) => applyTemplate(e.target.value)}
              style={{
                width: "100%",
                padding: "9px 12px",
                borderRadius: 8,
                fontSize: FONT_SIZES.sm,
                fontFamily: FONT_FAMILY,
                outline: "none",
                background: palette.inputBg,
                color: palette.inputColor,
                border: `1px solid ${palette.inputBorder}`,
              }}
            >
              <option value="">— No template —</option>
              {EMAIL_TEMPLATE_DEFINITIONS.map((def) => (
                <option key={def.key} value={def.key}>
                  {def.label} — {def.description}
                </option>
              ))}
            </select>
            {templateKey && (
              <div
                style={{
                  fontSize: 11,
                  color: palette.text3,
                  marginTop: 6,
                  fontFamily: FONT_FAMILY,
                }}
              >
                Subject and notes filled from the template. Edit freely
                before saving — you're not locked to the wording.
              </div>
            )}
          </div>
        )}

        {/* ── Subject ── */}
        <FormInput
          label="Subject"
          value={subject}
          onChange={setSubject}
          placeholder={`What was this ${
            typeInfo?.name.toLowerCase() || "contact"
          } about?`}
        />

        {/* ── Notes ── */}
        <div>
          <label
            style={{
              fontSize: FONT_SIZES.sm,
              fontWeight: 600,
              color: palette.text1,
              display: "block",
              marginBottom: 4,
            }}
          >
            Notes (optional)
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            placeholder="Key takeaways, next steps, follow-up items..."
            style={{
              width: "100%",
              padding: "10px 12px",
              borderRadius: 8,
              fontSize: FONT_SIZES.sm,
              fontFamily: FONT_FAMILY,
              outline: "none",
              background: palette.inputBg,
              color: palette.inputColor,
              border: `1px solid ${palette.inputBorder}`,
              resize: "vertical",
            }}
          />
        </div>

        {/* ── Contact Purpose (multi-select) ── */}
        <div>
          <label
            style={{
              fontSize: FONT_SIZES.sm,
              fontWeight: 600,
              color: palette.text1,
              display: "block",
              marginBottom: 8,
            }}
          >
            Purpose{" "}
            <span style={{ fontWeight: 400, color: palette.text3 }}>
              (optional — select all that apply)
            </span>
          </label>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {CONTACT_PURPOSES.map((cp) => {
              const isSelected = purpose.includes(cp.id);
              return (
                <button
                  key={cp.id}
                  onClick={() =>
                    setPurpose((prev) =>
                      isSelected
                        ? prev.filter((p) => p !== cp.id)
                        : [...prev, cp.id]
                    )
                  }
                  style={{
                    padding: "7px 14px",
                    borderRadius: 10,
                    cursor: "pointer",
                    transition: "all 0.2s",
                    border: isSelected
                      ? `2px solid ${cp.color}`
                      : `2px solid ${palette.cardBorder}`,
                    background: isSelected ? `${cp.color}12` : "transparent",
                    color: isSelected ? cp.color : palette.text1,
                    fontSize: FONT_SIZES.sm,
                    fontWeight: 600,
                    fontFamily: FONT_FAMILY,
                  }}
                >
                  {cp.name}
                </button>
              );
            })}
          </div>
        </div>

        {/* ── Email hint (outbound only) ── */}
        {type === "email" && direction === "outbound" && member.email && (
          <div
            style={{
              padding: "10px 14px",
              borderRadius: 8,
              background: "#dbeafe",
              border: "1px solid #93c5fd",
              fontSize: FONT_SIZES.xs,
              color: "#1e40af",
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <Send size={ICON_SIZES.sm} />
            Saving will also open your email client to{" "}
            <strong>{member.email}</strong>
          </div>
        )}

        {/* ── Actions ── */}
        <div
          style={{
            display: "flex",
            gap: 12,
            justifyContent: "flex-end",
            marginTop: 4,
          }}
        >
          <ActionButton label="Cancel" variant="secondary" onClick={onClose} />
          <ActionButton
            label={
              isEdit
                ? "Save Changes"
                : type === "email" && direction === "outbound" && member.email
                  ? "Log & Send Email"
                  : "Log Contact"
            }
            icon={Check}
            onClick={handleSave}
          />
        </div>
      </div>
    </Modal>
  );
};
